import { createSelector } from '@reduxjs/toolkit';

import { DialogAlert } from '@/shared/types/dialogAlert';
import { RootState } from '@/shared/types/state';

/**
 * Selects the dialog alerts slice from the root state
 */
export const selectDialogAlerts = (state: RootState) => state.dialogAlerts;

/**
 * Checks if there is an error alert for a given ID
 */
export const selectHasError =
  (id: string, key: keyof DialogAlert = 'id') =>
  (state: RootState) =>
    state.dialogAlerts.some((alert) => alert[key] === id && alert.alertType === 'error');

/**
 * Finds an error alert for a specific field and ID
 */
export const selectErrorByField = (id: string, field: string) => (state: RootState) =>
  state.dialogAlerts.find(
    (alert) => alert.id === id && alert.alertType === 'error' && alert.alertField === field
  );

/**
 * Checks if there is a warning alert for a given ID
 */
export const selectHasWarning =
  (id: string, key: keyof DialogAlert = 'id') =>
  (state: RootState) =>
    state.dialogAlerts.some((alert) => alert[key] === id && alert.alertType === 'warning');

/**
 * Checks if there are any carousel dialog errors for a given action ID
 */
export const selectHasCarouselDialogErrors = (actionId: string) => (state: RootState) =>
  state.dialogAlerts.some((error) => error.type === 'carousel' && error.id === actionId);

/**
 * Checks if there is an alert for a specific card button in a carousel
 */
export const isAlertThisCardButton =
  (cardIndex: number, buttonIndex: number) => (state: RootState) =>
    state.dialogAlerts.some(
      (alert) =>
        alert.type === 'carousel' &&
        alert.cardIndex === cardIndex &&
        alert.buttonIndex === buttonIndex
    );

/**
 * Checks if there are any error alerts in the dialog
 */
export const hasDialogAnyErrors = createSelector(selectDialogAlerts, (dialogAlerts) =>
  dialogAlerts.some((alert) => alert.alertType === 'error')
);
